/**
 * "A taxed line names its rate" — the one rule for every org, VAT and
 * SALES_TAX alike.
 *
 * A line with a positive `tax_rate` must carry its `tax_rate_id`. Posting no
 * longer guesses the rate from the percentage: two rates can share one (a US
 * state's Meals 9% and Rooms 9%; an NL org's "BTW 21%" and "BTW 21% events"),
 * and a guess either refused mid-posting or, in a VAT org, posted to an
 * "Unknown Rate" leg. The database is the authority (`post_signed_tax_for_line`
 * raises RV025 on an id-less taxed line); the api refuses the same document
 * at the edge with a coded 400 before any RPC runs
 * (apps/api/src/lib/taxedLineRateIds.ts, one call per document route), so a
 * recurring template is refused when it is saved, not at its next cron run.
 *
 * A 0% line (No tax, Exempt, zero-rated, reverse-charge at 0) is never taxed
 * and needs no id.
 */
/** The `code` of the api's 400 body for a document with an id-less taxed line. */
export const TAX_RATE_ID_REQUIRED = "TAX_RATE_ID_REQUIRED";
/**
 * The country of a jurisdiction code: its prefix before the first `-`,
 * trimmed and upper-cased (`US-VT` → `US`, `FR` → `FR`, none → `''`). The one
 * prefix rule every jurisdiction check reads — the same as the SQL's
 * (`_org_tax_jurisdiction` callers, 20261101130000).
 */
export function jurisdictionCountry(jurisdiction) {
    return (jurisdiction ?? "").trim().split("-")[0].toUpperCase();
}
/**
 * Whether a jurisdiction code is sales tax: its country
 * (`jurisdictionCountry`) is `US`, so a state code (`US-VT`) and a bare
 * country (`US`, an org with no saved tax setting) both are. Every other code,
 * and none yet, is VAT.
 */
export function isSalesTaxJurisdiction(jurisdiction) {
    return jurisdictionCountry(jurisdiction) === "US";
}
/** Indexes (0-based, input order) of the lines with `tax_rate > 0` and no `tax_rate_id`. */
export function taxedLinesWithoutRateId(lines) {
    const missing = [];
    lines.forEach((line, index) => {
        if ((line.tax_rate ?? 0) > 0 && !line.tax_rate_id)
            missing.push(index);
    });
    return missing;
}
/** The refusal for `lines`, or null when every taxed line names its rate. */
export function taxRateIdRefusal(lines) {
    const missing = taxedLinesWithoutRateId(lines);
    if (missing.length === 0)
        return null;
    const lineNumbers = missing.map((i) => i + 1).join(", ");
    return {
        code: TAX_RATE_ID_REQUIRED,
        error: `Pick the tax rate from the list on ${missing.length === 1 ? "line" : "lines"} ${lineNumbers}: ` +
            `a taxed line must name its tax rate.`,
        line_indexes: missing,
    };
}
/** The `code` of the api's 400 body when a line names a rate the org does not own (or cannot see). */
export const TAX_RATE_UNKNOWN = "TAX_RATE_UNKNOWN";
/**
 * Parse a database error message for the posting functions' tax-rate
 * refusals (`post_signed_tax_for_line`, `post_reverse_charge_for_line`,
 * 20261031100000) and code them:
 *
 *  - RV025 "Pick the tax rate on the line … : a taxed line must name its tax
 *    rate." → {@link TAX_RATE_ID_REQUIRED} (the same code the api's own
 *    pre-check answers with — the database is the backstop for a path the
 *    pre-check does not see);
 *  - 42501 "Unknown tax rate on line …" / "The tax rate on line … belongs to
 *    another organization." → {@link TAX_RATE_UNKNOWN}.
 *
 * The SQLSTATE does not survive the routes' `HTTPException(400, { message })`
 * pass-through, so — like `parseVatDeclared` — this keys on the text,
 * anywhere in the string (an RPC may wrap SQLERRM). `null` when it is neither.
 */
export function parseTaxRatePostingRefusal(message) {
    if (!message)
        return null;
    if (message.includes("a taxed line must name its tax rate")) {
        return { code: TAX_RATE_ID_REQUIRED, error: message };
    }
    if (message.includes("Unknown tax rate on line") ||
        /The tax rate on line .* belongs to another organization\./s.test(message)) {
        return { code: TAX_RATE_UNKNOWN, error: message };
    }
    return null;
}
//# sourceMappingURL=taxedLineRateIds.js.map